import type { ComponentProps, HTMLAttributes, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Chart } from '@highcharts/react';
import { StockChart } from '@highcharts/react/Stock';

import ChartSummary from '@/features/charts/ChartSummary';
import type { ChartSummaryProps } from '@/features/charts/ChartSummary';
import { useChartContext } from '@/features/charts/context/ChartContext';
import { mergeChartEvents } from '@/features/charts/hooks/options/useChartFunctions';
import type { ChartRef, ExtendedOptions } from '@/features/charts/types/highcharts';
import { Highcharts } from '@/shared/lib/highcharts';

import type { HighchartsReactRefObject } from '@highcharts/react';
import type HighchartsType from 'highcharts';

type HighchartsReactChartProps = ComponentProps<typeof Chart>;

type ChartShellProps = {
  options: ExtendedOptions | undefined;
  isStockChart?: boolean;
  chartId?: string;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  chartComponentRef?: RefObject<ChartRef | null>;
  onChartReady?: (chart: HighchartsType.Chart) => void;
  summary?: ChartSummaryProps;
};

type ChartReference = RefObject<ChartRef | null>;

type SyncChartReferencesParams = {
  chart: HighchartsType.Chart | undefined;
  chartRef: ChartReference;
  chartRefToUse: ChartReference;
  lastReadyChartRef: RefObject<HighchartsType.Chart | null>;
  onChartReady: ChartShellProps['onChartReady'];
};

const stripUpdateOnlyOptions = (options: ExtendedOptions): ExtendedOptions => {
  const { rangeSelector, ...rest } = options;
  return rangeSelector
    ? { ...rest, rangeSelector: { ...rangeSelector, selected: undefined } }
    : rest;
};

const syncChartReferences = ({
  chart,
  chartRef,
  chartRefToUse,
  lastReadyChartRef,
  onChartReady
}: SyncChartReferencesParams) => {
  if (!chart) return;

  const renderer = (chart as Partial<HighchartsType.Chart>).renderer;
  if (renderer?.forExport) return;

  chartRefToUse.current = { chart };
  chartRef.current = { chart };

  if (lastReadyChartRef.current === chart) return;

  lastReadyChartRef.current = chart;
  onChartReady?.(chart);
};

const addReadyHandler = (
  options: ExtendedOptions | undefined,
  handleChartReady: (this: HighchartsType.Chart) => void
) => {
  if (!options) return undefined;

  return {
    ...options,
    chart: {
      ...options.chart,
      events: mergeChartEvents(options.chart?.events, { load: handleChartReady })
    }
  };
};

const getChartOptions = (options: ExtendedOptions | undefined, hasMountedChart: boolean) => {
  if (!options || !hasMountedChart) return options;
  return stripUpdateOnlyOptions(options);
};

const getAccessibleContainerProps = (
  containerProps: ChartShellProps['containerProps'],
  hasSummary: boolean
) => (hasSummary ? { ...containerProps, 'aria-hidden': true } : containerProps);

const ChartShell = ({
  options,
  isStockChart = false,
  chartId,
  containerProps,
  chartComponentRef,
  onChartReady,
  summary
}: ChartShellProps) => {
  const internalRef = useRef<ChartRef | null>(null);
  const highchartsReactRef = useRef<HighchartsReactRefObject | null>(null);
  const lastReadyChartRef = useRef<HighchartsType.Chart | null>(null);
  const chartRefToUse = chartComponentRef ?? internalRef;
  const { chartRef } = useChartContext();

  const syncChartRef = useCallback(
    (chart: HighchartsType.Chart | undefined) => {
      syncChartReferences({
        chart,
        chartRef,
        chartRefToUse,
        lastReadyChartRef,
        onChartReady
      });
    },
    [chartRef, chartRefToUse, onChartReady]
  );

  const handleChartReady = useCallback(
    function (this: HighchartsType.Chart) {
      syncChartRef(this);
    },
    [syncChartRef]
  );

  const optionsWithReadyHandler = useMemo(
    () => addReadyHandler(options, handleChartReady),
    [options, handleChartReady]
  );

  const chartOptions = useMemo(
    () => getChartOptions(optionsWithReadyHandler, Boolean(chartRefToUse.current)),
    [chartRefToUse, optionsWithReadyHandler]
  );

  useEffect(() => {
    syncChartRef(highchartsReactRef.current?.chart);
  }, [chartOptions, syncChartRef]);

  useEffect(
    () => () => {
      chartRefToUse.current = null;
      chartRef.current = null;
      lastReadyChartRef.current = null;
    },
    [chartRef, chartRefToUse]
  );

  if (!chartOptions) return null;

  const ChartComponent = isStockChart ? StockChart : Chart;
  const highchartsInstance = Highcharts as unknown as HighchartsReactChartProps['highcharts'];
  const highchartsOptions = chartOptions as unknown as HighchartsReactChartProps['options'];
  const accessibleContainerProps = getAccessibleContainerProps(containerProps, Boolean(summary));
  const chart = (
    <ChartComponent
      key={chartId}
      ref={highchartsReactRef}
      containerProps={accessibleContainerProps}
      highcharts={highchartsInstance}
      options={highchartsOptions}
    />
  );

  if (!summary) return chart;

  return (
    <div className='relative w-full'>
      <div className='sr-only'>
        <ChartSummary {...summary} />
      </div>
      {chart}
    </div>
  );
};

export default ChartShell;
